import i18n from 'i18next';
import { isInKorea } from './regionDetector';
import config from 'config/config';

export interface RouteStep {
    instruction: string;       // 안내 문구 (예: "좌회전 후 100m 직진")
    distance: number;          // 이 구간 거리 (m)
    duration: number;          // 이 구간 소요 시간 (초)
    type: 'straight' | 'left' | 'right' | 'uturn' | 'arrive' | 'depart' | 'merge' | 'fork' | 'roundabout' | 'other';
    location?: [number, number]; // [lat, lng]
    roadName?: string;
}

export interface RouteResult {
    duration: number;    // 소요 시간 (초)
    distance: number;    // 거리 (m)
    coordinates: [number, number][]; // [lat, lng] 경로 좌표 배열
    steps?: RouteStep[]; // 턴바이턴 안내
}

/**
 * 길찾기 API 호출 (한국: 카카오, 해외: OSRM)
 * 반환되는 경로 좌표의 양 끝점을 실제 origin/destination 으로 보정해
 * 마커와 선 사이에 시각적 공백이 생기지 않도록 처리 (Kakao/OSRM 모두 최단 도로점에 스냅함)
 */
export async function getRoute(
    origin: { lat: number; lng: number },
    destination: { lat: number; lng: number }
): Promise<RouteResult | null> {
    try {
        const result = isInKorea(origin.lat, origin.lng)
            ? await getKakaoRoute(origin, destination)
            : await getOsrmRoute(origin, destination);
        if (!result || result.coordinates.length === 0) return result;

        // 양 끝을 실제 출발지/목적지로 클램프 (가까우면 중복 제거)
        const APPROX_SAME_DEG = 1e-6;
        const same = (a: [number, number], b: [number, number]) =>
            Math.abs(a[0] - b[0]) < APPROX_SAME_DEG && Math.abs(a[1] - b[1]) < APPROX_SAME_DEG;

        const originPt: [number, number] = [origin.lat, origin.lng];
        const destPt: [number, number] = [destination.lat, destination.lng];
        const coords = [...result.coordinates];
        if (!same(coords[0], originPt)) coords.unshift(originPt);
        if (!same(coords[coords.length - 1], destPt)) coords.push(destPt);
        return { ...result, coordinates: coords };
    } catch (e) {
        console.error('[Route] 길찾기 실패:', e);
        return null;
    }
}

/**
 * 카카오 길찾기 API (자동차 경로)
 */
async function getKakaoRoute(
    origin: { lat: number; lng: number },
    destination: { lat: number; lng: number }
): Promise<RouteResult | null> {
    const KAKAO_KEY = config.KAKAO_REST_KEY || config.KAKAO_MAP_KEY;
    if (!KAKAO_KEY) return getOsrmRoute(origin, destination); // fallback

    const url = `https://apis-navi.kakaomobility.com/v1/directions?origin=${origin.lng},${origin.lat}&destination=${destination.lng},${destination.lat}&summary=false&waypoints=&priority=RECOMMEND`;

    const res = await fetch(url, {
        headers: { 'Authorization': `KakaoAK ${KAKAO_KEY}` }
    });

    if (!res.ok) return getOsrmRoute(origin, destination); // fallback

    const data = await res.json();
    const route = data?.routes?.[0];
    if (!route || route.result_code !== 0) return null;

    // sections → roads → vertexes 에서 좌표 추출
    const coords: [number, number][] = [];
    const steps: RouteStep[] = [];
    route.sections?.forEach((section: any) => {
        section.roads?.forEach((road: any) => {
            const v = road.vertexes;
            if (v) {
                for (let i = 0; i < v.length; i += 2) {
                    coords.push([v[i + 1], v[i]]); // [lat, lng]
                }
            }
        });
        // guides 에서 턴바이턴 안내 추출
        section.guides?.forEach((g: any) => {
            steps.push({
                instruction: g.guidance || g.name || i18n.t('route.continue'),
                distance: g.distance || 0,
                duration: g.duration || 0,
                type: kakaoGuideType(g.type),
                location: typeof g.y === 'number' && typeof g.x === 'number' ? [g.y, g.x] : undefined,
                roadName: g.name || undefined,
            });
        });
    });

    return {
        duration: route.summary?.duration || 0, // 초
        distance: route.summary?.distance || 0, // m
        coordinates: coords,
        steps: steps.length > 0 ? steps : undefined,
    };
}

// 카카오 guide type 코드 → 통합 타입 매핑
// 1=직진, 2=좌회전, 3=우회전, 4=U턴, 5/6=좌측/우측방향, 7~16=좌/우합류·분기, 100=출발지, 101=목적지
function kakaoGuideType(code: number): RouteStep['type'] {
    if (code === 1) return 'straight';
    if (code === 2 || code === 5 || code === 7 || code === 9) return 'left';
    if (code === 3 || code === 6 || code === 8 || code === 10) return 'right';
    if (code === 4) return 'uturn';
    if (code === 100) return 'depart';
    if (code === 101) return 'arrive';
    if (code >= 11 && code <= 16) return 'merge';
    if (code >= 17 && code <= 20) return 'roundabout';
    return 'other';
}

/**
 * OSRM 길찾기 API (무료, 해외용)
 */
async function getOsrmRoute(
    origin: { lat: number; lng: number },
    destination: { lat: number; lng: number }
): Promise<RouteResult | null> {
    const url = `https://router.project-osrm.org/route/v1/driving/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?overview=full&geometries=geojson&steps=true`;

    const res = await fetch(url);
    if (!res.ok) return null;

    const data = await res.json();
    const route = data?.routes?.[0];
    if (!route) return null;

    // GeoJSON coordinates: [lng, lat] → [lat, lng]
    const coords: [number, number][] = (route.geometry?.coordinates || []).map(
        (c: [number, number]) => [c[1], c[0]]
    );

    // legs[].steps[] → 턴바이턴
    const steps: RouteStep[] = [];
    route.legs?.forEach((leg: any) => {
        leg.steps?.forEach((s: any) => {
            const m = s.maneuver || {};
            const type = osrmManeuverType(m.type, m.modifier);
            const loc: [number, number] | undefined =
                Array.isArray(m.location) && m.location.length === 2
                    ? [m.location[1], m.location[0]]
                    : undefined;
            steps.push({
                instruction: osrmInstruction(type, s.name),
                distance: s.distance || 0,
                duration: s.duration || 0,
                type,
                location: loc,
                roadName: s.name || undefined,
            });
        });
    });

    return {
        duration: route.duration || 0,
        distance: route.distance || 0,
        coordinates: coords,
        steps: steps.length > 0 ? steps : undefined,
    };
}

function osrmManeuverType(type?: string, modifier?: string): RouteStep['type'] {
    if (type === 'depart') return 'depart';
    if (type === 'arrive') return 'arrive';
    if (type === 'merge') return 'merge';
    if (type === 'fork') return 'fork';
    if (type === 'roundabout' || type === 'rotary') return 'roundabout';
    if (modifier === 'uturn') return 'uturn';
    if (modifier?.includes('left')) return 'left';
    if (modifier?.includes('right')) return 'right';
    if (modifier === 'straight') return 'straight';
    return 'other';
}

function osrmInstruction(type: RouteStep['type'], name?: string): string {
    const road = name ? `${name} ` : '';
    switch (type) {
        case 'depart': return i18n.t('route.depart', { road });
        case 'arrive': return i18n.t('route.arrive');
        case 'left': return i18n.t('route.left', { road });
        case 'right': return i18n.t('route.right', { road });
        case 'uturn': return i18n.t('route.uturn', { road });
        case 'straight': return i18n.t('route.straight', { road });
        case 'merge': return i18n.t('route.merge', { road });
        case 'fork': return i18n.t('route.fork', { road });
        case 'roundabout': return i18n.t('route.roundabout', { road });
        default: return road ? i18n.t('route.proceed', { name }) : i18n.t('route.continue');
    }
}

/**
 * 소요 시간 포맷
 */
export function formatDuration(seconds: number): string {
    if (seconds < 60) return i18n.t('route.under1min');
    const mins = Math.round(seconds / 60);
    if (mins < 60) return i18n.t('route.aboutMin', { mins });
    const hours = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return remainMins > 0 ? i18n.t('route.aboutHourMin', { hours, mins: remainMins }) : i18n.t('route.aboutHour', { hours });
}
